/**
 * Admin availability panel — manages the recurring weekly templates that
 * power the public booking calendar.
 */
import React, { useEffect, useState } from "react";
import { api } from "../api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DURATIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "60 min" },
];

export default function AvailabilityPanel({ token }) {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const data = await api.adminListTemplates(token);
      setTpls(data);
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function remove(id) {
    if (!window.confirm("Delete this availability slot?")) return;
    try {
      await api.adminDeleteTemplate(token, id);
      setTpls((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function toggleActive(t) {
    try {
      const updated = await api.adminUpdateTemplate(token, t.id, {
        ...t,
        active: !t.active,
      });
      setTpls((prev) => prev.map((p) => (p.id === t.id ? updated : p)));
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <section className="mt-12" data-testid="admin-availability-panel">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
            Availability
          </div>
          <h2 className="font-display text-3xl text-[#1C1C1C] tracking-tighter font-light">
            Weekly schedule
          </h2>
          <p className="text-sm text-[#1C1C1C]/60 mt-1">
            Set the recurring windows when students can book lessons (UTC).
          </p>
        </div>
        <button
          data-testid="admin-availability-add-btn"
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
        >
          + Add slot
        </button>
      </div>

      {error && <div className="text-sm text-[#B83A3A] mb-4">{error}</div>}

      {loading ? (
        <div className="text-[#1C1C1C]/60">Loading…</div>
      ) : tpls.length === 0 ? (
        <div className="p-6 bg-white border border-[#1C1C1C]/10 rounded-2xl text-center text-[#1C1C1C]/60" data-testid="admin-availability-empty">
          No availability set yet — add one to start accepting bookings.
        </div>
      ) : (
        <div className="space-y-2">
          {tpls.map((t) => (
            <div
              key={t.id}
              data-testid={`admin-availability-row-${t.id}`}
              className="flex items-center justify-between bg-white border border-[#1C1C1C]/10 rounded-xl px-5 py-3"
            >
              <div className="flex items-center gap-4">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 w-10">
                  {DAYS[t.day_of_week]}
                </div>
                <div className="font-mono text-sm">
                  {t.start_time} → {t.end_time}
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55">
                  {t.duration_minutes}-min slots
                </div>
                {!t.active && (
                  <span className="text-[9px] tracking-[0.22em] uppercase bg-stone-100 text-stone-700 px-2 py-0.5 rounded">
                    Inactive
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleActive(t)}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] px-2 py-1"
                  data-testid={`admin-availability-toggle-${t.id}`}
                >
                  {t.active ? "Disable" : "Enable"}
                </button>
                <button
                  onClick={() => remove(t.id)}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#B83A3A]/80 hover:text-[#B83A3A] px-2 py-1"
                  data-testid={`admin-availability-delete-${t.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddTemplateModal
          token={token}
          onClose={() => setShowAdd(false)}
          onAdded={(t) => {
            setTpls((prev) => [...prev, t].sort((a, b) =>
              a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time),
            ));
            setShowAdd(false);
          }}
        />
      )}
    </section>
  );
}

function AddTemplateModal({ token, onClose, onAdded }) {
  const [dow, setDow] = useState(0);
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("11:00");
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const t = await api.adminCreateTemplate(token, {
        day_of_week: dow,
        start_time: start,
        end_time: end,
        duration_minutes: duration,
        active: true,
      });
      onAdded(t);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="admin-availability-modal">
      <div className="w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-[#1C1C1C]">Add availability</h2>
          <button onClick={onClose} className="text-[#1C1C1C]/60 hover:text-[#1C1C1C]">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">Day</div>
            <select
              data-testid="admin-availability-day"
              value={dow}
              onChange={(e) => setDow(parseInt(e.target.value))}
              className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-[#1C1C1C]/10 rounded-xl"
            >
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">Start (UTC)</div>
              <input data-testid="admin-availability-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-[#1C1C1C]/10 rounded-xl" />
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">End (UTC)</div>
              <input data-testid="admin-availability-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-[#1C1C1C]/10 rounded-xl" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">Slot duration</div>
            <select
              data-testid="admin-availability-duration"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-[#1C1C1C]/10 rounded-xl"
            >
              {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
          {error && <div className="text-sm text-[#B83A3A]" data-testid="admin-availability-error">{error}</div>}
          <button
            data-testid="admin-availability-submit"
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Adding…" : "Add"}
          </button>
        </form>
      </div>
    </div>
  );
}
